/**
 * TELA 01 — INÍCIO / VITRINE
 * Destaques do Comida di Buteco, atalhos rápidos funcionais e categorias na ordem homologada.
 */

import { STORE_INFO, CATEGORIES, PRODUCTS } from '../data/menuData.js';
import { formatCurrency, formatPriceDisplay } from '../utils/formatters.js';

export function renderHomeView(container, store) {
  const diButecoItems = PRODUCTS.filter(p => p.categoryId === 'di-buteco');
  // Di Buteco tem seção de destaque exclusivo, não repetir na grade de categorias comuns
  const regularCategories = CATEGORIES.filter(c => c.id !== 'di-buteco');

  container.innerHTML = `
    <!-- Hero Principal do Estabelecimento com Identidade Forte -->
    <header class="hero-banner">
      <div class="hero-logo-container">
        <img src="${STORE_INFO.logo}" alt="Logo Oficial ${STORE_INFO.name}" class="hero-logo-large" />
      </div>
      
      <div class="hero-brand-block">
        <h1 class="hero-title">${STORE_INFO.name}</h1>
        <p class="hero-tagline text-gold">${STORE_INFO.tagline}</p>
      </div>
      
      <div class="hero-meta">
        <div class="hero-address-item">
          <svg class="icon-pin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          <span>${STORE_INFO.address}</span>
        </div>
        <span class="hero-meta-divider">•</span>
        <span class="badge badge-gold">✨ Cardápio Digital Oficial</span>
      </div>

      <!-- 5 Atalhos Rápidos Funcionais -->
      <div class="quick-shortcuts" role="navigation" aria-label="Atalhos rápidos do cardápio">
        <button id="btn-shortcut-lanche" class="shortcut-btn" type="button">
          <span class="shortcut-title">🍔 Quero um lanche</span>
          <span class="shortcut-subtitle">Tradicionais & Artesanais</span>
        </button>

        <button id="btn-shortcut-jantinha" class="shortcut-btn" type="button">
          <span class="shortcut-title">🍢 Quero uma jantinha</span>
          <span class="shortcut-subtitle">Monte com acompanhamentos</span>
        </button>

        <button id="btn-shortcut-pizza" class="shortcut-btn" type="button">
          <span class="shortcut-title">🍕 Quero uma pizza</span>
          <span class="shortcut-subtitle">Clássicas, Especiais & Doces</span>
        </button>

        <button id="btn-shortcut-bebidas" class="shortcut-btn" type="button">
          <span class="shortcut-title">🍺 Quero bebidas</span>
          <span class="shortcut-subtitle">Cervejas, Chopp & Drinks</span>
        </button>

        <button id="btn-shortcut-caldo" class="shortcut-btn" type="button">
          <span class="shortcut-title">🥣 Quero um caldo</span>
          <span class="shortcut-subtitle">Caldos quentinhos da casa</span>
        </button>
      </div>
    </header>

    <!-- Destaque Exclusivo: Comida di Buteco -->
    <section class="section-container" style="margin-bottom: 36px;" aria-labelledby="heading-dibuteco">
      <div class="section-header">
        <div class="section-title-wrap">
          <img src="${STORE_INFO.diButecoBadge}" alt="Selo Oficial Comida di Buteco" class="dibuteco-badge-header" />
          <div>
            <h2 id="heading-dibuteco" class="section-title">Comida di Buteco</h2>
            <p style="font-size: 0.82rem; color: var(--text-muted);">Pratos concorrentes oficiais do maior concurso de boteco do Brasil</p>
          </div>
        </div>
      </div>

      <div class="dibuteco-grid">
        ${diButecoItems.map(item => `
          <article class="dibuteco-card" data-product-id="${item.id}">
            <div class="dibuteco-img-wrap">
              <img src="${item.image}" alt="${item.name}" loading="lazy" />
              <div class="dibuteco-stamp">
                <img src="${STORE_INFO.diButecoBadge}" alt="Comida di Buteco" />
              </div>
            </div>
            <div class="dibuteco-content">
              <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
                <h3 style="font-size: 1.2rem; font-weight: 800; color: var(--text-primary);">${item.name}</h3>
                <span class="badge badge-fire">Oficial</span>
              </div>
              <p style="font-size: 0.85rem; color: var(--text-secondary); line-height: 1.45; flex: 1;">${item.description}</p>
              
              <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 8px; padding-top: 12px; border-top: 1px dashed var(--border-subtle);">
                <div>
                  <span style="font-size: 0.72rem; color: var(--text-muted); display: block; text-transform: uppercase;">Valor do Prato</span>
                  <span class="product-price ${item.pricePending ? 'product-price-pending' : ''}">
                    ${formatPriceDisplay(item.price, item.pricePending, item.priceLabel)}
                  </span>
                </div>
                <button class="btn btn-sm btn-primary btn-order-dibuteco" data-product-id="${item.id}" type="button">
                  Pedir Prato
                </button>
              </div>
            </div>
          </article>
        `).join('')}
      </div>
    </section>

    <!-- Seção de Categorias do Cardápio (Sem repetir Di Buteco) -->
    <section class="section-container" aria-labelledby="heading-categories">
      <div class="section-header">
        <div>
          <h2 id="heading-categories" class="section-title">Cardápio por Categoria</h2>
          <p style="font-size: 0.82rem; color: var(--text-muted); margin-top: 2px;">Escolha uma categoria para explorar os itens</p>
        </div>
        <span class="badge badge-gold">${regularCategories.length} categorias</span>
      </div>

      <div class="categories-grid">
        ${regularCategories.map(cat => {
          const count = PRODUCTS.filter(p => p.categoryId === cat.id).length;
          return `
            <article class="category-card" data-category-id="${cat.id}" tabindex="0" role="button" aria-label="Abrir categoria ${cat.name}">
              <div class="category-card-img-wrap">
                <img src="${cat.banner}" alt="Banner ${cat.name}" class="category-card-img" loading="lazy" />
                <span class="category-badge-count">${count} itens</span>
              </div>
              <div class="category-card-body">
                <h3 class="category-card-name">${cat.name}</h3>
                <p class="category-card-tagline">${cat.tagline}</p>
                <div class="category-card-action">
                  <span>Ver opções</span>
                  <span aria-hidden="true">→</span>
                </div>
              </div>
            </article>
          `;
        }).join('')}
      </div>
    </section>
  `;

  // Event Listeners dos 5 Atalhos Rápidos
  container.querySelector('#btn-shortcut-lanche')?.addEventListener('click', () => {
    store.setView('category', { categoryId: 'lanches' });
  });

  container.querySelector('#btn-shortcut-jantinha')?.addEventListener('click', () => {
    store.setView('jantinha');
  });

  container.querySelector('#btn-shortcut-pizza')?.addEventListener('click', () => {
    store.setView('category', { categoryId: 'pizzas' });
  });

  container.querySelector('#btn-shortcut-bebidas')?.addEventListener('click', () => {
    store.setView('category', { categoryId: 'bebidas' });
  });

  container.querySelector('#btn-shortcut-caldo')?.addEventListener('click', () => {
    store.setView('category', { categoryId: 'caldos' });
  });

  // Listener para pedidos do Comida di Buteco
  container.querySelectorAll('.btn-order-dibuteco').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const productId = e.currentTarget.getAttribute('data-product-id');
      const product = PRODUCTS.find(p => p.id === productId);
      if (product) {
        store.setView('product', { product });
      }
    });
  });

  // Listener de clique nas categorias
  container.querySelectorAll('.category-card').forEach(card => {
    const handleCategoryClick = () => {
      const categoryId = card.getAttribute('data-category-id');
      if (categoryId === 'jantinhas') {
        store.setView('jantinha');
      } else {
        store.setView('category', { categoryId });
      }
    };

    card.addEventListener('click', handleCategoryClick);
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCategoryClick();
      }
    });
  });
}
